import type { Balance } from 'stake-engine';
import { apiStore } from '../stores/api.store';
import type { APIController } from '../utils/api.types';

/**
 * How often the wallet balance is re-requested while the game sits idle. The
 * poll exists for wallet changes the game can't see (a deposit, another tab, an
 * operator adjustment); every change the game itself causes arrives with a play
 * or end round response, so there is nothing to gain from a tighter interval —
 * the RGS client polls itself only once a minute.
 */
const POLL_INTERVAL = 10_000;

/**
 * Keeps the displayed balance in step with the wallet.
 *
 * Play and end round responses carry the authoritative balance, so those are
 * pushed in through {@link apply}. Everything else only shows up in a balance
 * request, which is what the timer is for.
 *
 * The poll deliberately stays quiet while a round is in play: the wallet can't
 * change under the player mid-round, and a poll landing on the payout would
 * replace the counting-up balance with the value it was requested with.
 */
export class BalanceController {
    private timer?: ReturnType<typeof setInterval>;
    private inFlight: Promise<void> | null = null;
    /**
     * Bumped by every balance that arrives with a game response. A poll that
     * started before that response is stale and must not be applied — the RGS
     * answered it with the pre-bet (or pre-payout) balance.
     */
    private generation = 0;
    /** A failing poll logs once, not on every tick. */
    private reportedFailure = false;

    constructor(private api: APIController) {}

    /** Starts polling. Safe to call again — the previous timer is replaced. */
    start() {
        this.stop();
        this.timer = setInterval(() => {
            if (this.idle()) void this.refresh();
        }, POLL_INTERVAL);
    }

    /**
     * Nothing is being played or presented: the round is settled and the game is
     * waiting for a spin. Both halves are needed — the game state goes back to
     * `idle` on the book's finalWin, while the round it belongs to is only
     * settled (and its payout credited) once the sequence has played out.
     */
    private idle(): boolean {
        const { state, roundActive } = apiStore.getState();

        return state === 'idle' && !roundActive;
    }

    stop() {
        clearInterval(this.timer);
        this.timer = undefined;
    }

    /** Applies the balance returned by a game response, dropping any poll in flight. */
    apply(balance?: Balance) {
        if (!balance) return;

        this.generation++;
        apiStore.getState().actions.setBalance(balance);
    }

    /**
     * Requests the balance now. Concurrent calls share the one request, so a
     * manual refresh landing on a poll tick doesn't double up.
     */
    refresh(): Promise<void> {
        this.inFlight ??= this.request().finally(() => {
            this.inFlight = null;
        });

        return this.inFlight;
    }

    private async request(): Promise<void> {
        const generation = this.generation;

        try {
            const balance = await this.api.balance();

            // a play/end round response landed while this was in flight — its
            // balance is newer than ours
            if (generation !== this.generation) return;

            apiStore.getState().actions.setBalance(balance);
            this.reportedFailure = false;
        } catch (error) {
            if (!this.reportedFailure) {
                console.error('Failed to refresh balance:', error);
                this.reportedFailure = true;
            }
        }
    }
}
